import { useMemo, useState } from 'react';
import { cellText, downloadCsv, filterRows, safeHref, sortRows, toCsv, type Column, type Row } from '../table';

interface Props {
  columns: Column[];
  rows: Row[];
  /** Base name for downloads, e.g. "replit-projects-2026-11-04". */
  filename: string;
}

export function DataTable({ columns, rows, filename }: Props) {
  const [search, setSearch] = useState('');
  const [perColumn, setPerColumn] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | undefined>();

  const view = useMemo(() => {
    const filtered = filterRows(rows, columns, search, perColumn);
    if (!sort) return filtered;
    return sortRows(filtered, sort.key, sort.dir, columns.find((c) => c.key === sort.key)?.type);
  }, [rows, columns, search, perColumn, sort]);

  const toggleSort = (key: string) =>
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : undefined));
  const filtering = search.trim() !== '' || Object.values(perColumn).some((v) => v.trim());
  const order = columns.map((c) => c.key);

  return (
    <div className="table-wrap">
      <div className="toolbar">
        <input
          type="search"
          placeholder="Search all columns"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search all columns"
        />
        <span className="count">
          {view.length === rows.length ? `${rows.length} rows` : `${view.length} of ${rows.length} rows`}
        </span>
        {filtering && (
          <button type="button" onClick={() => { setSearch(''); setPerColumn({}); }}>Clear filters</button>
        )}
        <span className="spacer" />
        <button type="button" onClick={() => downloadCsv(`${filename}${filtering ? '-filtered' : ''}.csv`, toCsv(view, order))}>
          Download {filtering ? 'filtered ' : ''}CSV
        </button>
        {filtering && (
          <button type="button" onClick={() => downloadCsv(`${filename}.csv`, toCsv(rows, order))}>Download all</button>
        )}
      </div>
      <div className="scroller">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className="sort" onClick={() => toggleSort(c.key)}>
                    {c.label}
                    <span className="arrow">{sort?.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
                  </button>
                </th>
              ))}
            </tr>
            <tr className="filters">
              {columns.map((c) => (
                <th key={c.key}>
                  <input
                    value={perColumn[c.key] ?? ''}
                    onChange={(e) => setPerColumn((f) => ({ ...f, [c.key]: e.target.value }))}
                    placeholder="Filter"
                    aria-label={`Filter ${c.label}`}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => <Cell key={c.key} column={c} value={row[c.key]} />)}
              </tr>
            ))}
          </tbody>
        </table>
        {view.length === 0 && <p className="empty">No rows match.</p>}
      </div>
    </div>
  );
}

function Cell({ column, value }: { column: Column; value: unknown }) {
  const text = cellText(value, column.type);
  if (column.type === 'url') {
    const href = safeHref(value);
    return <td>{href ? <a href={href} target="_blank" rel="noopener noreferrer">{href.replace(/^https?:\/\//, '')}</a> : text}</td>;
  }
  if (column.type === 'severity') return <td><span className={`sev sev-${text}`}>{text}</span></td>;
  if (column.type === 'bool') return <td className={`bool bool-${text}`}>{text}</td>;
  if (column.type === 'date') return <td title={value ? String(value) : undefined}>{text}</td>;
  return <td>{text}</td>;
}
